import { RootStateType } from '@Store';
import { Box, Chip, Grid, Typography, Paper, useTheme, alpha } from '@mui/material';
import { CalendarToday, LocalShipping, Person } from '@mui/icons-material';
import moment from 'moment';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import DownloadShipmentLabelAndInvoice from '@Components/DownloadShipmentLabelAndInvoice';
import ShipmentKycUpload from '@Components/ShipmentKycUpload';
import { CSP_TYPES } from '@Constants/Shipment';

function Overview({ EditPermissionAllowed }: { EditPermissionAllowed?: boolean }) {
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment?.shipmentById);
  const [documentUploadModalOpen, setDocumentUploadModalOpen] = useState(false);
  const theme = useTheme();

  if (!shipmentById) return <></>;

  const shipmentNumber = shipmentById?.shipmentNumber || '';
  const WritePermission = EditPermissionAllowed ?? true;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2,
        borderRadius: '14px',
        width: '100%',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.06)',
          borderColor: alpha(theme.palette.primary.main, 0.25),
        },
      }}
    >
      <Grid container spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid item xs={12} md={2.5} lg={2.2}>
          <ShipmentNumberAndManifest />
        </Grid>

        <Grid item xs={12} md={5} lg={4}>
          <ShipmentPlaceDetails />
        </Grid>

        <Grid item xs={12} md={4} lg={3}>
          <PlaceDate />
        </Grid>

        <Grid item xs={12} md={12} lg={2.5}>
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}>
            {WritePermission && (
              <DownloadShipmentLabelAndInvoice
                handleView={() => setDocumentUploadModalOpen(true)}
                shipmentNumber={shipmentById?.shipmentNumber}
                carrierAWBIcon={
                  shipmentById?.vendorShipmentModel &&
                  (shipmentById?.buyingPricing?.code === CSP_TYPES.fedex.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.ithinklogistics.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.ithink_logistics_domestic.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.linex.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.delhivery.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.trackon.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.movin_ups.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.dpduk.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.dhlIndia.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.dhl.value ||
                    shipmentById?.buyingPricing?.code === 'cirro' ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.stage.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.universal.value ||
                    shipmentById?.buyingPricing?.code === CSP_TYPES.aramex.value)
                    ? shipmentById?.buyingPricing?.serviceImage
                    : ''
                }
              />
            )}
          </Box>
        </Grid>
      </Grid>

      {documentUploadModalOpen && (
        <ShipmentKycUpload
          shipmentNumber={shipmentNumber}
          open={documentUploadModalOpen}
          handleClose={() => setDocumentUploadModalOpen(false)}
        />
      )}
    </Paper>
  );
}

function ShipmentNumberAndManifest() {
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.5px',
            color: theme.palette.text.primary,
            lineHeight: 1.3,
            mb: 0.25,
          }}
        >
          {shipmentById?.shipmentNumber || '—'}
        </Typography>
        <Chip
          label={`Manifest: ${shipmentById?.manifestNumber || '—'}`}
          size="small"
          variant="outlined"
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            borderColor: alpha(theme.palette.secondary.main, 0.5),
            color: theme.palette.secondary.main,
            height: 24,
            borderRadius: 1.5,
            backdropFilter: 'blur(8px)',
            background: alpha(theme.palette.secondary.main, 0.05),
            '& .MuiChip-label': { px: 1.2 },
          }}
        />
      </Box>
    </Box>
  );
}

function ShipmentPlaceDetails() {
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const placedBy = shipmentById?._placedById;
  const onBehalfBy = shipmentById?._userId;
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.25,
          borderRadius: '10px',
          bgcolor: alpha(theme.palette.primary.main, 0.03),
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        }}
      >
        <Person sx={{ fontSize: 20, color: 'primary.main' }} />
        <Box>
          <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 500, display: 'block' }}>
            Placed by
          </Typography>
          <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'common.black' }}>
            {placedBy?.name || '—'}
            <Box
              component="span"
              sx={{
                ml: 1,
                px: 1,
                py: 0.25,
                borderRadius: '6px',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: 'primary.main',
                fontSize: '0.75rem',
                fontWeight: 600,
              }}
            >
              {placedBy?.accessGroup === 'pu_all' ? placedBy?.email : placedBy?.username || '—'}
            </Box>
          </Typography>
        </Box>
      </Box>

      {placedBy?.accessGroup !== 'pu_all' && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.25,
            borderRadius: '10px',
            bgcolor: alpha(theme.palette.secondary.main, 0.03),
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <LocalShipping sx={{ fontSize: 20, color: 'secondary.main' }} />
          <Box>
            <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 500, display: 'block' }}>
              On behalf of
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'common.black' }}>
              {onBehalfBy?.name || '—'}
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 1,
                  py: 0.25,
                  borderRadius: '6px',
                  bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  color: 'secondary.main',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                {onBehalfBy?.email || '—'}
              </Box>
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function PlaceDate() {
  const shipmentById = useSelector((state: RootStateType) => state.adminShipment.shipmentById);
  const date = shipmentById?.createdAt ? moment(shipmentById?.createdAt).format('DD MMM YYYY, hh:mm a') : '';
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        borderRadius: '10px',
        bgcolor: alpha(theme.palette.primary.main, 0.03),
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      }}
    >
      <CalendarToday sx={{ fontSize: 20, color: 'primary.main' }} />
      <Box>
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 500, display: 'block' }}>
          Created at
        </Typography>
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            color: 'common.black',
          }}
        >
          {date || '—'}
        </Typography>
      </Box>
    </Box>
  );
}

export default Overview;
